/**
 * 메뉴 항목 비가용(sold_out / 마감 / 영업시간 외 / 오늘 불가) 상태 라벨·토스트 15-locale 사전.
 *
 * #menu-ui TASK-11(ISSUE-13): MenuItemCard 의 인라인 `switch(language)`(ko/es/en 3값) 비가용 텍스트를
 * `locale`(15) 기반 사전으로 이관. `Record<Locale, ...>` 타입이 15 로케일 전수 존재를 컴파일 강제.
 *
 * ⚠️ 신규 12 로케일(pt/fr/zh/ja/hi/de/it/ru/ar/vi/th/nl) 번역은 표준 어휘 기반 1차 작성 —
 * 네이티브 감수 권장(특히 ar/th/hi). ko/es/en 은 기존 인라인 값 보존.
 */
import type { Locale } from '@/config/locales';

export interface MenuItemAvailabilityTranslation {
  /** 품절 라벨(오버레이) */
  soldOut: string;
  /** 매장 마감 라벨 */
  storeClosed: string;
  /** 영업시간 외 라벨(메뉴/카테고리 시간 외 · display disabled 공용) */
  outsideHours: string;
  /** 그 외 당일 불가 fallback 라벨 */
  notToday: string;
  /** 품절 클릭 토스트 */
  soldOutToast: string;
  /** 다음 판매 시각 안내 토스트 ({nextAvailable} 치환) */
  availableAfter: string;
  /** 현재 판매시간 아님 토스트(시각 미정) */
  notAvailableNow: string;
  /** 일반 주문 불가 토스트 */
  notAvailableGeneric: string;
}

export const menuItemAvailabilityTranslations: Record<Locale, MenuItemAvailabilityTranslation> = {
  es: {
    soldOut: 'Agotado',
    storeClosed: 'Cerrado',
    outsideHours: 'Fuera de horario',
    notToday: 'Hoy no',
    soldOutToast: 'Este artículo está agotado.',
    availableAfter: 'Disponible después de {nextAvailable}.',
    notAvailableNow: 'No disponible en este horario.',
    notAvailableGeneric: 'No disponible.',
  },
  en: {
    soldOut: 'Sold Out',
    storeClosed: 'Closed',
    outsideHours: 'Outside hours',
    notToday: 'Not today',
    soldOutToast: 'This item is sold out.',
    availableAfter: 'Available after {nextAvailable}.',
    notAvailableNow: 'Not available at this time.',
    notAvailableGeneric: 'Not available.',
  },
  ko: {
    soldOut: '품절',
    storeClosed: '매장 마감',
    outsideHours: '영업시간 외',
    notToday: '오늘 불가',
    soldOutToast: '품절된 메뉴입니다.',
    availableAfter: '{nextAvailable} 후 판매를 시작합니다.',
    notAvailableNow: '현재 판매하지 않는 시간입니다.',
    notAvailableGeneric: '현재 주문할 수 없습니다.',
  },
  pt: {
    soldOut: 'Esgotado',
    storeClosed: 'Fechado',
    outsideHours: 'Fora do horário',
    notToday: 'Hoje não',
    soldOutToast: 'Este item está esgotado.',
    availableAfter: 'Disponível após {nextAvailable}.',
    notAvailableNow: 'Não disponível neste horário.',
    notAvailableGeneric: 'Não disponível.',
  },
  fr: {
    soldOut: 'Épuisé',
    storeClosed: 'Fermé',
    outsideHours: 'Hors des heures',
    notToday: "Pas aujourd'hui",
    soldOutToast: 'Cet article est épuisé.',
    availableAfter: 'Disponible après {nextAvailable}.',
    notAvailableNow: 'Non disponible à cette heure.',
    notAvailableGeneric: 'Non disponible.',
  },
  zh: {
    soldOut: '售罄',
    storeClosed: '已打烊',
    outsideHours: '非营业时间',
    notToday: '今日不可',
    soldOutToast: '该商品已售罄。',
    availableAfter: '{nextAvailable} 后开始供应。',
    notAvailableNow: '当前时段不可用。',
    notAvailableGeneric: '当前不可下单。',
  },
  ja: {
    soldOut: '売り切れ',
    storeClosed: '閉店',
    outsideHours: '営業時間外',
    notToday: '本日不可',
    soldOutToast: 'この商品は売り切れです。',
    availableAfter: '{nextAvailable} 以降に販売を開始します。',
    notAvailableNow: '現在は販売時間外です。',
    notAvailableGeneric: '現在ご注文いただけません。',
  },
  hi: {
    soldOut: 'स्टॉक समाप्त',
    storeClosed: 'बंद',
    outsideHours: 'समय के बाहर',
    notToday: 'आज नहीं',
    soldOutToast: 'यह वस्तु स्टॉक में नहीं है।',
    availableAfter: '{nextAvailable} के बाद उपलब्ध।',
    notAvailableNow: 'इस समय उपलब्ध नहीं।',
    notAvailableGeneric: 'उपलब्ध नहीं।',
  },
  de: {
    soldOut: 'Ausverkauft',
    storeClosed: 'Geschlossen',
    outsideHours: 'Außerhalb der Zeiten',
    notToday: 'Heute nicht',
    soldOutToast: 'Dieser Artikel ist ausverkauft.',
    availableAfter: 'Verfügbar ab {nextAvailable}.',
    notAvailableNow: 'Zu dieser Zeit nicht verfügbar.',
    notAvailableGeneric: 'Nicht verfügbar.',
  },
  it: {
    soldOut: 'Esaurito',
    storeClosed: 'Chiuso',
    outsideHours: 'Fuori orario',
    notToday: 'Oggi no',
    soldOutToast: 'Questo articolo è esaurito.',
    availableAfter: 'Disponibile dopo {nextAvailable}.',
    notAvailableNow: 'Non disponibile in questo orario.',
    notAvailableGeneric: 'Non disponibile.',
  },
  ru: {
    soldOut: 'Распродано',
    storeClosed: 'Закрыто',
    outsideHours: 'Вне часов работы',
    notToday: 'Не сегодня',
    soldOutToast: 'Этот товар распродан.',
    availableAfter: 'Доступно после {nextAvailable}.',
    notAvailableNow: 'Недоступно в это время.',
    notAvailableGeneric: 'Недоступно.',
  },
  ar: {
    soldOut: 'نفد',
    storeClosed: 'مغلق',
    outsideHours: 'خارج ساعات العمل',
    notToday: 'ليس اليوم',
    soldOutToast: 'هذا الصنف نفد.',
    availableAfter: 'متاح بعد {nextAvailable}.',
    notAvailableNow: 'غير متاح في هذا الوقت.',
    notAvailableGeneric: 'غير متاح.',
  },
  vi: {
    soldOut: 'Hết hàng',
    storeClosed: 'Đã đóng cửa',
    outsideHours: 'Ngoài giờ',
    notToday: 'Hôm nay không',
    soldOutToast: 'Món này đã hết hàng.',
    availableAfter: 'Có sẵn sau {nextAvailable}.',
    notAvailableNow: 'Không khả dụng vào lúc này.',
    notAvailableGeneric: 'Không khả dụng.',
  },
  th: {
    soldOut: 'สินค้าหมด',
    storeClosed: 'ปิด',
    outsideHours: 'นอกเวลาทำการ',
    notToday: 'วันนี้ไม่ได้',
    soldOutToast: 'สินค้านี้หมดแล้ว',
    availableAfter: 'พร้อมจำหน่ายหลัง {nextAvailable}',
    notAvailableNow: 'ไม่พร้อมจำหน่ายในเวลานี้',
    notAvailableGeneric: 'ไม่พร้อมจำหน่าย',
  },
  nl: {
    soldOut: 'Uitverkocht',
    storeClosed: 'Gesloten',
    outsideHours: 'Buiten openingstijden',
    notToday: 'Vandaag niet',
    soldOutToast: 'Dit artikel is uitverkocht.',
    availableAfter: 'Beschikbaar na {nextAvailable}.',
    notAvailableNow: 'Niet beschikbaar op dit moment.',
    notAvailableGeneric: 'Niet beschikbaar.',
  },
};
